import { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import api from "../api/api";
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, BarChart, Bar, Cell } from 'recharts';

export default function Dashboard() {
  const [data, setData] = useState(null);
  const [financeData, setFinanceData] = useState(null); 
  const [revenueData, setRevenueData] = useState(null);
  const [extraRevenues, setExtraRevenues] = useState([]);
  
  const [dailyTasks, setDailyTasks] = useState([]);
  const [showReportsHub, setShowReportsHub] = useState(false);
  
  // 🚀 NOVO ESTADO: Controle dos Rankings
  const [showRankings, setShowRankings] = useState(false);
  
  const [allToners, setAllToners] = useState([]);
  const [search, setSearch] = useState("");
  const [tonerPeriod, setTonerPeriod] = useState("month");

  const [selectedMonth, setSelectedMonth] = useState(() => {
    const today = new Date();
    return `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}`;
  });

  const COLORS = ['#4f46e5', '#0ea5e9', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899', '#2dd4bf', '#fb7185'];

  const fetchDashboard = async (client = search, month = selectedMonth) => {
    try {
      let url = `/dashboard?month=${month}`;
      if (client) url += `&client_name=${client}`;
      
      // 🚀 A ordem aqui é sagrada: [0]Dash, [1]Fin, [2]Rev, [3]Extra, [4]Tasks, [5]Toners
      const responses = await Promise.all([
        api.get(url),
        api.get(`/finance/costs?month=${month}`).catch(() => ({ data: null })),
        api.get(`/readings/revenue-analysis?month=${month}`).catch(() => ({ data: { current_total: 0, by_client: [] } })),
        api.get(`/readings/extra-revenues?month=${month}`).catch(() => ({ data: [] })),
        api.get(`/tasks/`).catch(() => ({ data: [] })),
        api.get(`/toner`).catch(() => ({ data: [] })) 
      ]);
      
      setData(responses[0].data);
      if (responses[1].data) setFinanceData(responses[1].data);
      setRevenueData(responses[2].data);
      setExtraRevenues(responses[3].data || []);
      setDailyTasks(responses[4].data?.filter(t => t.status === 'pendente') || []);
      setAllToners(responses[5].data || []); // 🚀 Agora o dado chega no lugar certo
      
    } catch (err) {
      console.error("Erro Dashboard:", err);
    }
  };

  // 🚀 NOVA FUNÇÃO: Concluir tarefa direto do Dashboard
  const handleCompleteDailyTask = async (taskId) => {
    try {
      await api.put(`/tasks/${taskId}/toggle`);
      fetchDashboard(); // Recarrega para sumir da tela
    } catch (err) {
      alert("Erro ao concluir a tarefa.");
    }
  };

  const formatDataForChart = (originalData) => {
    return originalData?.map(item => ({
      ...item,
      shortName: item.name ? item.name.split(' ')[0] : item.client ? item.client.split(' ')[0] : "Sem Nome",
      count: Number(item.count || item.cost || 0) 
    })) || [];
  };

  const formattedTonerRanking = useMemo(() => formatDataForChart(data?.toner_ranking), [data?.toner_ranking]);
  const formattedMaintRanking = useMemo(() => formatDataForChart(data?.maint_ranking), [data?.maint_ranking]);
  const formattedFinanceRanking = useMemo(() => formatDataForChart(financeData?.client_costs), [financeData?.client_costs]);
  
  const healthMetrics = useMemo(() => {
    if (!data || !financeData || !revenueData) return null;
    const machinesRevenue = revenueData.current_total || 0;
    const extraRevenueTotal = extraRevenues.reduce((acc, curr) => acc + Number(curr.amount), 0);
    const grandTotalRevenue = machinesRevenue + extraRevenueTotal;
    const totalCost = financeData.total_cost || 0;
    const netProfit = grandTotalRevenue - totalCost;
    const criticalToners = data.toner_inventory?.filter(t => t.stock < 2) || [];
    return { netProfit, criticalToners };
  }, [data, financeData, revenueData, extraRevenues]);

  const zeroStockToners = useMemo(() => {
    return allToners.filter(t => t.stock <= 0);
  }, [allToners]);

  // 🚀 AGRUPAMENTO DE LOTES (MÁGICA QUE JUNTA O CLIENTE NUM QUADRADO SÓ)
  const groupedPendingShipments = useMemo(() => {
    if (!data?.pending_shipments) return [];
    const groups = {};
    data.pending_shipments.forEach(item => {
      const client = item.client || "Desconhecido";
      if (!groups[client]) {
        groups[client] = { client, descriptions: [], batchIds: [] };
      }
      groups[client].descriptions.push(item.items_desc);
      groups[client].batchIds.push(item.batch_id);
    });
    return Object.values(groups);
  }, [data?.pending_shipments]);

  // 🚀 BOTÕES ATUALIZADOS PARA LIDAR COM O LOTE INTEIRO (ARRAY DE IDS)
  const handleConfirmBatch = async (batchIds) => {
    if (!batchIds || batchIds.length === 0) return alert("Nenhum item selecionado");
    try {
      // Limpa IDs duplicados caso o backend tenha gerado batch_id iguais
      const uniqueIds = [...new Set(batchIds)];
      await Promise.all(uniqueIds.map(id => api.put(`/dashboard/delivery/batch/${id}/confirm`)));
      fetchDashboard();
    } catch (err) { alert("Erro ao confirmar lote"); }
  };

  const handleCancelBatch = async (batchIds) => {
    if (!batchIds || batchIds.length === 0) return;
    if (!window.confirm("Deseja cancelar o envio de TODOS os toners deste cliente?")) return;
    try {
      const uniqueIds = [...new Set(batchIds)];
      await Promise.all(uniqueIds.map(id => api.delete(`/dashboard/delivery/batch/${id}`)));
      fetchDashboard();
    } catch (err) { alert("Erro ao cancelar lote."); }
  };

  useEffect(() => { fetchDashboard(search, selectedMonth); }, [selectedMonth]);

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    fetchDashboard(search, selectedMonth);
  };

  const formatCurrency = (val) => new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(val || 0);

  if (!data) return (
    <div className="flex h-[80vh] items-center justify-center flex-col gap-4">
        <div className="w-10 h-10 border-4 border-indigo-200 border-t-indigo-600 rounded-full animate-spin"></div>
        <p className="font-black text-indigo-900 uppercase tracking-widest text-[10px]">Sincronizando Banco de Dados...</p>
    </div>
  );

  return (
    <div className="animate-in fade-in slide-in-from-bottom-4 duration-700 p-3 md:p-4 space-y-4 pb-10 bg-transparent min-h-screen relative">
      
      {/* HEADER PREMIUM - COMPACTADO */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 bg-white/70 backdrop-blur-xl p-5 rounded-3xl border border-white/50 shadow-sm hover:shadow-md transition-all duration-500">
        <div>
          <h1 className="text-3xl font-black text-slate-900 tracking-tighter leading-none">Dash<span className="text-transparent bg-clip-text bg-gradient-to-r from-indigo-600 to-violet-500">Board</span></h1>
          <p className="text-[9px] text-slate-500 font-black uppercase tracking-[0.2em] mt-1">Alta Performance</p>
        </div>
        
        <div className="flex flex-col sm:flex-row items-center gap-3 w-full md:w-auto">
          <button 
            onClick={() => setShowReportsHub(true)}
            className="w-full sm:w-auto px-4 py-2.5 bg-slate-900 text-white rounded-xl text-[10px] font-black uppercase tracking-widest hover:bg-indigo-600 transition-all shadow-md active:scale-95 flex items-center justify-center gap-2"
          >
            <span className="text-sm">🧭</span> Menu
          </button>

          <form onSubmit={handleSearchSubmit} className="flex flex-col sm:flex-row items-center gap-3 w-full md:w-auto">
            <input 
              type="month"
              className="w-full sm:w-auto px-4 py-2.5 bg-white/80 border border-slate-200/80 rounded-xl text-xs font-black outline-none focus:bg-white focus:ring-2 ring-indigo-500/20 transition-all shadow-inner text-slate-700 cursor-pointer uppercase tracking-widest"
              value={selectedMonth}
              onChange={(e) => setSelectedMonth(e.target.value)}
            />
            <div className="relative group w-full sm:w-[280px]">
              <input 
                type="text" 
                placeholder="Filtrar empresa..."
                className="w-full pl-10 pr-20 py-2.5 bg-white/80 border border-slate-200/80 rounded-xl text-xs font-bold outline-none focus:bg-white focus:ring-2 ring-indigo-500/20 transition-all shadow-inner placeholder:text-slate-400"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
              <span className="absolute left-3 top-2.5 text-sm opacity-40 group-focus-within:text-indigo-600 transition-colors">🔍</span>
              <button type="submit" className="absolute right-1 top-1 bg-slate-900 text-white px-4 py-1.5 rounded-lg text-[9px] font-black uppercase tracking-widest hover:bg-indigo-600 transition-all shadow-sm active:scale-95">Filtrar</button>
            </div>
          </form>
        </div>
      </div>

      {/* KPIs RÁPIDOS - COMPACTADOS */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        
        {/* NOVO CARD: TAREFAS A CONCLUIR */}
        <div className="bg-gradient-to-br from-indigo-500 to-blue-600 p-5 rounded-2xl text-white shadow-sm relative overflow-hidden group hover:-translate-y-1 transition-all">
          <div className="absolute -right-4 -top-4 w-24 h-24 bg-white/10 rounded-full blur-xl group-hover:scale-125 transition-transform"></div>
          <p className="text-[9px] font-black uppercase tracking-widest mb-1 opacity-90 flex items-center gap-2">
            <span className="w-1.5 h-1.5 bg-blue-200 rounded-full animate-pulse shadow-[0_0_8px_rgba(255,255,255,0.8)]"></span> Tarefas a Concluir
          </p>
          <h3 className="text-2xl lg:text-3xl font-black relative z-10 tracking-tighter">{dailyTasks.length}</h3>
          <div className="absolute right-4 top-1/2 -translate-y-1/2 text-3xl opacity-20">📋</div>
        </div>

        <div className="bg-white/60 backdrop-blur-md p-5 rounded-2xl border border-white/60 shadow-sm hover:shadow-md hover:-translate-y-1 transition-all flex flex-col justify-center relative overflow-hidden group">
          <div className="absolute top-0 left-0 w-1.5 h-full bg-indigo-500"></div>
          <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest mb-1 ml-2">Impressoras</p>
          <h3 className="text-2xl lg:text-3xl font-black text-slate-800 ml-2">{data?.metrics?.total_printers || 0}</h3>
          <div className="absolute right-4 top-1/2 -translate-y-1/2 text-3xl opacity-10">🖨️</div>
        </div>

        <div className="bg-white/60 backdrop-blur-md p-5 rounded-2xl border border-white/60 shadow-sm hover:shadow-md hover:-translate-y-1 transition-all flex flex-col justify-center relative overflow-hidden group">
          <div className="absolute top-0 left-0 w-1.5 h-full bg-rose-500"></div>
          <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest mb-1 ml-2">Pendentes</p>
          <h3 className="text-2xl lg:text-3xl font-black text-rose-500 ml-2">{data?.metrics?.open || 0}</h3>
          <div className="absolute right-4 top-1/2 -translate-y-1/2 text-3xl opacity-10">🛠️</div>
        </div>

        <div className="bg-slate-950 p-5 rounded-2xl border border-slate-800 text-white shadow-md hover:shadow-lg hover:-translate-y-1 transition-all flex flex-col justify-center relative overflow-hidden group">
          <div className="absolute right-0 top-0 w-24 h-24 bg-indigo-500/10 rounded-full blur-xl"></div>
          <p className="text-[9px] font-black text-indigo-300 uppercase tracking-widest mb-1 relative z-10">📦 A Enviar</p>
          <h3 className="text-2xl lg:text-3xl font-black relative z-10">{data?.pending_shipments?.length || 0} Kits</h3>
        </div>
      </div>

      {/* ================================================================= */}
      {/* SEÇÃO 1: NÚCLEO DE OPERAÇÕES DIÁRIAS (ULTRA COMPACTADO)           */}
      {/* ================================================================= */}
      <div className="space-y-4">

        {/* 🚨 1º LUGAR: ALERTA DE TONERS ZERADOS (COMPACTO) */}
        {zeroStockToners.length > 0 && (
          <div className="bg-gradient-to-br from-rose-50 to-red-50 border border-rose-200/60 p-4 rounded-2xl shadow-sm relative overflow-hidden">
            <div className="absolute -left-2 top-0 bottom-0 w-1.5 bg-rose-500"></div>
            <h2 className="text-[10px] font-black text-rose-700 uppercase tracking-widest mb-3 flex items-center gap-2">
               <span className="text-sm animate-pulse">🚨</span> Urgente: Insumos Zerados
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-2">
              {zeroStockToners.map((t, i) => (
                <div key={i} className="bg-white/90 p-2.5 rounded-xl border border-rose-200/80 flex flex-col justify-between gap-1 shadow-sm hover:shadow-md hover:border-rose-300 transition-all group">
                  <div>
                    <p className="font-black text-slate-800 text-[10px] leading-tight uppercase group-hover:text-rose-600 transition-colors line-clamp-1" title={t.model}>
                      {t.model}
                    </p>
                    <p className="text-[8px] text-slate-500 font-bold truncate">{t.description}</p>
                  </div>
                  <div className="mt-1 pt-1.5 border-t border-rose-100 flex justify-between items-center">
                    <span className="text-[9px] font-black text-slate-400">R$ {Number(t.cost_price).toFixed(2).replace('.', ',')}</span>
                    <span className="bg-rose-500 text-white px-2 py-0.5 rounded-md text-[8px] font-black uppercase shadow-sm animate-pulse">
                      0 UN
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
        
        {/* 🚚 2º LUGAR: LOGÍSTICA KITS AGUARDANDO SAÍDA (COMPACTO) */}
        {groupedPendingShipments.length > 0 && (
          <div className="bg-gradient-to-br from-amber-50 to-orange-50 border border-amber-200/60 p-4 rounded-2xl shadow-sm relative overflow-hidden">
            <div className="absolute -left-2 top-0 bottom-0 w-1.5 bg-amber-400"></div>
            <h2 className="text-[10px] font-black text-amber-700 uppercase tracking-widest mb-3 flex items-center gap-2">
               <span className="text-sm animate-bounce">🚚</span> Logística: Saídas Pendentes
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-2.5">
              {groupedPendingShipments.map((group, i) => (
                <div key={i} className="bg-white/80 p-3 rounded-xl border border-amber-100/80 flex flex-col justify-between shadow-sm hover:shadow-md transition-all">
                  <div>
                    <p className="text-[9px] font-black text-indigo-600 uppercase mb-1.5 truncate border-b border-indigo-100 pb-1" title={group.client}>{group.client}</p>
                    <div className="space-y-1 mb-2 max-h-16 overflow-y-auto custom-scrollbar pr-1">
                      {group.descriptions.map((desc, idx) => (
                        <p key={idx} className="font-bold text-slate-800 text-[9px] leading-tight uppercase flex items-center gap-1.5">
                          <span className="w-1.5 h-1.5 bg-amber-400 rounded-full flex-shrink-0"></span> <span className="truncate" title={desc}>{desc}</span>
                        </p>
                      ))}
                    </div>
                  </div>
                  <div className="flex gap-1.5 mt-auto pt-2 border-t border-amber-100/50">
                    <button onClick={() => handleCancelBatch(group.batchIds)} className="flex-1 bg-slate-50 text-slate-400 py-1.5 rounded-md text-[8px] font-black uppercase hover:bg-rose-50 hover:text-rose-600 border border-slate-200 transition-all">Cancelar</button>
                    <button onClick={() => handleConfirmBatch(group.batchIds)} className="flex-1 bg-amber-500 text-white py-1.5 rounded-md text-[8px] font-black uppercase hover:bg-amber-600 transition-all shadow-sm">Confirmar</button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* TAREFAS AVULSAS PENDENTES COM CHECK DIRETO */}
        {dailyTasks.length > 0 && (
          <div className="bg-white/80 backdrop-blur-sm border border-indigo-100 p-4 rounded-2xl shadow-sm relative">
            <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-indigo-400 to-cyan-400"></div>
            <div className="flex justify-between items-center mb-3">
              <h2 className="text-[10px] font-black text-slate-800 uppercase tracking-widest flex items-center gap-2">
                <span className="text-sm">📋</span> Tarefas Pendentes
              </h2>
              <Link to="/tasks" className="text-[9px] font-black text-indigo-600 uppercase hover:text-indigo-800">Ver Painel →</Link>
            </div>
            
            <div className="flex overflow-x-auto gap-3 pb-2 custom-scrollbar">
              {dailyTasks.map(task => (
                <div key={task.id} className="min-w-[200px] w-[200px] bg-slate-50 border border-slate-100 p-3 rounded-xl flex-shrink-0 hover:bg-white hover:shadow-md transition-all flex flex-col justify-between">
                  <div>
                    <div className="flex justify-between items-start mb-1.5">
                      <span className="text-[8px] font-black uppercase tracking-widest text-indigo-600 bg-indigo-50 border border-indigo-100 px-2 py-0.5 rounded-md">{task.category}</span>
                      <button onClick={() => handleCompleteDailyTask(task.id)} className="w-5 h-5 rounded-full bg-slate-200 text-slate-400 flex items-center justify-center font-black text-[10px] hover:bg-emerald-500 hover:text-white transition-all">✓</button>
                    </div>
                    <h3 className="font-black text-slate-800 text-[10px] mb-1 uppercase leading-tight line-clamp-1">{task.title}</h3>
                    <p className="text-[8px] text-slate-500 font-bold line-clamp-1">{task.description || "Sem observação."}</p>
                  </div>
                  {task.price > 0 && <p className="text-[10px] font-black text-emerald-600 mt-1.5 pt-1.5 border-t border-slate-200/50">{formatCurrency(task.price)}</p>}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ALERTA DE ESTOQUE CRÍTICO */}
        {healthMetrics?.criticalToners.length > 0 && (
          <div className="bg-rose-50/80 border border-rose-200 p-4 rounded-2xl shadow-sm flex flex-col md:flex-row md:items-center gap-4">
            <h2 className="text-[10px] font-black text-rose-600 uppercase tracking-widest flex items-center gap-2 whitespace-nowrap">
               <span className="relative flex h-2 w-2"><span className="animate-ping absolute h-full w-full rounded-full bg-rose-400 opacity-75"></span><span className="relative rounded-full h-2 w-2 bg-rose-500"></span></span>
               Estoque Crítico ({"<"} 2 UN)
            </h2>
            <div className="flex flex-wrap gap-2">
              {healthMetrics.criticalToners.map((t, i) => (
                <div key={i} className="bg-white text-rose-700 px-2.5 py-1 rounded-md font-black text-[8px] uppercase border border-rose-200 shadow-sm flex items-center gap-1.5">
                  <span>{t.model}</span><span className="text-rose-600 bg-rose-50 px-1.5 py-0.5 rounded">{t.stock} UN</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* 🛠️ CARDS DE MANUTENÇÃO - COMPACTADOS LADO A LADO */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          
          <div className="bg-white/70 backdrop-blur-md rounded-2xl border border-white/60 shadow-sm flex flex-col">
            <div className="p-3 border-b border-rose-100 bg-rose-50/50 flex justify-between text-rose-600 font-black uppercase text-[9px] tracking-widest">
              Chamados Abertos <span className="w-1.5 h-1.5 bg-rose-500 rounded-full animate-pulse mt-0.5"></span>
            </div>
            <div className="divide-y divide-slate-100 max-h-[200px] overflow-y-auto custom-scrollbar flex-1">
              {data?.pending_maintenance?.length > 0 ? data.pending_maintenance.map((m, i) => (
                <div key={i} className="p-3 flex justify-between items-center hover:bg-slate-50 transition-all">
                  <div>
                    <p className="text-[8px] font-black text-indigo-600 uppercase mb-0.5 truncate w-40">{m.client_name}</p>
                    <p className="font-black text-slate-800 text-[10px] uppercase leading-none mb-1">{m.model}</p>
                    <p className="text-[8px] text-rose-600 font-black uppercase tracking-widest bg-rose-50 px-1.5 py-0.5 rounded-md inline-block">{m.issue}</p>
                  </div>
                  <p className="text-[8px] font-bold text-slate-400">{new Date(m.date).toLocaleDateString()}</p>
                </div>
              )) : <div className="p-8 text-center text-slate-400 font-black uppercase text-[9px]">Oficina limpa! 🎉</div>}
            </div>
          </div>

          <div className="bg-white/70 backdrop-blur-md rounded-2xl border border-white/60 shadow-sm flex flex-col">
            <div className="p-3 border-b border-emerald-100 bg-emerald-50/50 flex justify-between text-emerald-600 font-black uppercase text-[9px] tracking-widest">
              Serviços Concluídos <span>✓</span>
            </div>
            <div className="divide-y divide-slate-100 max-h-[200px] overflow-y-auto custom-scrollbar flex-1">
              {data?.fixed_printers?.length > 0 ? data.fixed_printers.map((p, i) => (
                <div key={i} className="p-3 flex justify-between items-center hover:bg-slate-50 transition-all">
                  <div className="pr-2">
                    <p className="text-[8px] font-black text-indigo-600 uppercase mb-0.5 truncate w-40">{p.client_name}</p>
                    <p className="font-black text-slate-800 text-[10px] uppercase leading-none mb-1">{p.model}</p>
                    <p className="text-[8px] text-slate-500 font-bold uppercase truncate w-48">{p.action}</p>
                  </div>
                  <p className="text-[8px] font-bold text-slate-400">{new Date(p.date).toLocaleDateString()}</p>
                </div>
              )) : <div className="p-8 text-center text-slate-400 font-black uppercase text-[9px]">Sem serviços no período.</div>}
            </div>
          </div>
        </div>
      </div>

      {/* BOTÃO DE DIVISÃO */}
      <div className="flex justify-center pt-2 pb-1">
        <button 
          onClick={() => setShowRankings(!showRankings)} 
          className={`px-5 py-2 rounded-full font-black text-[9px] uppercase tracking-widest transition-all shadow-sm flex items-center gap-2 border
            ${showRankings ? 'bg-slate-100 text-slate-500 border-slate-200' : 'bg-slate-900 text-white border-slate-900 hover:bg-indigo-600'}`}
        >
          {showRankings ? '✕ Ocultar Gráficos' : '📊 Expandir Gráficos'}
        </button>
      </div>

      {/* ================================================================= */}
      {/* SEÇÃO 2: RANKINGS E GRÁFICOS (COMPACTADA)                         */}
      {/* ================================================================= */}
      {showRankings && (
        <div className="space-y-4 animate-in slide-in-from-top-4 fade-in duration-500">
          
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {/* RANKING TONER */}
            <div className="bg-white/70 p-5 rounded-2xl border border-white/60 shadow-sm relative overflow-hidden">
              <h3 className="text-[10px] font-black uppercase text-slate-400 tracking-widest mb-4 text-center">🏆 Ranking Toner no Mês</h3>
              <div className="h-48 w-full">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={formattedTonerRanking} layout="vertical" margin={{ right: 30, left: 0 }}>
                    <XAxis type="number" hide />
                    <YAxis dataKey="shortName" type="category" axisLine={false} tickLine={false} tick={{fill: '#1e293b', fontSize: 9, fontWeight: 900}} width={70} />
                    <Tooltip cursor={{fill: 'transparent'}} contentStyle={{ borderRadius: '10px', fontSize: '12px' }} />
                    <Bar dataKey="count" radius={[0, 8, 8, 0]} barSize={16} label={{ position: 'right', fill: '#94a3b8', fontSize: 9, fontWeight: 900 }}>
                      {formattedTonerRanking.map((entry, index) => <Cell key={`cell-toner-${index}`} fill={COLORS[index % COLORS.length]} />)}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>

            {/* ÚLTIMAS ENTREGAS */}
            <div className="bg-[#0a0a0a] rounded-2xl p-5 text-white shadow-lg flex flex-col h-[260px]">
              <div className="flex justify-between items-center mb-3 border-b border-white/10 pb-2">
                <h2 className="text-[10px] font-black uppercase tracking-widest text-indigo-400">Últimas Entregas</h2>
                <span className="bg-white/10 px-2 py-0.5 rounded-md text-[8px] font-black uppercase">{data?.recent_toners?.length || 0} Itens</span>
              </div>
              <div className="flex-1 overflow-y-auto space-y-1.5 pr-1 custom-scrollbar">
                {data?.recent_toners?.map((t, i) => (
                  <div key={i} className="flex gap-2.5 items-center bg-white/5 p-2 rounded-xl">
                    <div className="bg-white/10 w-7 h-7 rounded-lg flex items-center justify-center font-black text-[10px] text-indigo-300">{t.qty}</div>
                    <div className="flex-1">
                      <div className="flex justify-between items-center mb-0.5">
                        <p className="text-[8px] text-indigo-400 font-black uppercase truncate w-32">{t.client}</p>
                        <p className="text-[8px] text-white/40">{new Date(t.date).toLocaleDateString('pt-BR')}</p>
                      </div>
                      <p className="text-[9px] font-black uppercase truncate text-slate-200">{t.item}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* CUSTOS E FLUXOS NUMA GRID COMPACTA */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            
            {/* GRÁFICO DE CUSTOS */}
            {financeData && financeData.client_costs.length > 0 && (
              <div className="bg-white/70 p-5 rounded-2xl border border-white/60 shadow-sm">
                <div className="flex justify-between items-center mb-4">
                  <div>
                    <h3 className="text-[10px] font-black uppercase text-slate-800 tracking-widest">Custo por Cliente</h3>
                  </div>
                  <div className="text-right">
                    <p className="text-[8px] font-black text-rose-400 uppercase">Custo Total</p>
                    <span className="text-base font-black text-rose-500">{formatCurrency(financeData.total_cost)}</span>
                  </div>
                </div>
                <div className="h-48 w-full">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={formattedFinanceRanking} layout="vertical" margin={{ right: 60, left: 0 }}>
                      <XAxis type="number" hide />
                      <YAxis dataKey="shortName" type="category" axisLine={false} tickLine={false} tick={{fill: '#1e293b', fontSize: 9, fontWeight: 900}} width={70} />
                      <Tooltip formatter={(value) => formatCurrency(value)} cursor={{fill: 'transparent'}} contentStyle={{ fontSize: '11px', borderRadius: '10px' }} />
                      <Bar dataKey="count" radius={[0, 8, 8, 0]} barSize={16} label={{ position: 'right', fill: '#f43f5e', fontSize: 9, fontWeight: 900, formatter: (val) => formatCurrency(val) }}>
                        {formattedFinanceRanking.map((entry, index) => <Cell key={`cell-cost-${index}`} fill="#f43f5e" />)}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>
            )}

            {/* GRÁFICO HISTÓRICO 6 MESES */}
            <div className="bg-[#0a0a0a] text-white p-5 rounded-2xl shadow-lg border border-white/5 flex flex-col justify-center">
                <h2 className="text-[10px] font-black text-indigo-400 uppercase tracking-widest mb-4 text-center">Histórico Operacional (6 meses)</h2>
                <div className="h-48 w-full">
                    <ResponsiveContainer width="100%" height="100%">
                        <AreaChart data={data?.time_series || []} margin={{ top: 5, right: 10, left: -20, bottom: 0 }}>
                            <XAxis dataKey="month" axisLine={false} tickLine={false} tick={{fill: '#64748b', fontSize: 9}} />
                            <YAxis axisLine={false} tickLine={false} tick={{fill: '#64748b', fontSize: 9}} />
                            <Tooltip contentStyle={{ backgroundColor: 'rgba(10, 10, 10, 0.95)', fontSize: '11px', borderRadius: '10px' }} itemStyle={{color: '#fff'}} />
                            <Area type="monotone" dataKey="total" stroke="#8b5cf6" fillOpacity={0.2} fill="#8b5cf6" strokeWidth={2} dot={{ r: 3, fill: '#8b5cf6' }} />
                        </AreaChart>
                    </ResponsiveContainer>
                </div>
            </div>
          </div>
        </div>
      )}

      {/* 🚀 MODAL: HUB CENTRAL */}
      {showReportsHub && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm animate-in fade-in" onClick={() => setShowReportsHub(false)}>
          <div className="bg-white w-full max-w-2xl rounded-3xl p-6 shadow-2xl relative" onClick={(e) => e.stopPropagation()}>
            <button onClick={() => setShowReportsHub(false)} className="absolute top-4 right-4 w-8 h-8 bg-slate-100 rounded-full flex items-center justify-center text-slate-500 hover:bg-rose-100 hover:text-rose-600 font-black">✕</button>
            <div className="mb-5">
              <h2 className="text-2xl font-black text-slate-800 tracking-tighter">Menu <span className="text-indigo-600">Rápido</span></h2>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <Link to="/tasks" className="p-3 bg-slate-50 rounded-xl border border-slate-200 hover:border-indigo-400 hover:shadow-md transition-all">
                <div className="w-8 h-8 bg-indigo-100 text-indigo-600 rounded-lg flex items-center justify-center text-lg mb-2">📋</div>
                <h3 className="font-black text-slate-800 text-[10px] uppercase">Tarefas</h3>
              </Link>
              <Link to="/faturamento" className="p-3 bg-slate-50 rounded-xl border border-slate-200 hover:border-emerald-400 hover:shadow-md transition-all">
                <div className="w-8 h-8 bg-emerald-100 text-emerald-600 rounded-lg flex items-center justify-center text-lg mb-2">💰</div>
                <h3 className="font-black text-slate-800 text-[10px] uppercase">Faturamento</h3>
              </Link>
              <Link to="/custos" className="p-3 bg-slate-50 rounded-xl border border-slate-200 hover:border-rose-400 hover:shadow-md transition-all">
                <div className="w-8 h-8 bg-rose-100 text-rose-600 rounded-lg flex items-center justify-center text-lg mb-2">💸</div>
                <h3 className="font-black text-slate-800 text-[10px] uppercase">Custos</h3>
              </Link>
              <Link to="/faturas" className="p-3 bg-slate-50 rounded-xl border border-slate-200 hover:border-amber-400 hover:shadow-md transition-all">
                <div className="w-8 h-8 bg-amber-100 text-amber-600 rounded-lg flex items-center justify-center text-lg mb-2">🧾</div>
                <h3 className="font-black text-slate-800 text-[10px] uppercase">Faturas</h3>
              </Link>
              <Link to="/toner" className="p-3 bg-slate-50 rounded-xl border border-slate-200 hover:border-cyan-400 hover:shadow-md transition-all">
                <div className="w-8 h-8 bg-cyan-100 text-cyan-600 rounded-lg flex items-center justify-center text-lg mb-2">📦</div>
                <h3 className="font-black text-slate-800 text-[10px] uppercase">Estoque</h3>
              </Link>
              <Link to="/calendario" className="p-3 bg-slate-50 rounded-xl border border-slate-200 hover:border-violet-400 hover:shadow-md transition-all">
                <div className="w-8 h-8 bg-violet-100 text-violet-600 rounded-lg flex items-center justify-center text-lg mb-2">📅</div>
                <h3 className="font-black text-slate-800 text-[10px] uppercase">Agenda</h3>
              </Link>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}